/**
 * Load More Pagination Component
 * 基于 Web Component 的无刷新分页组件
 * 避免重复绑定，支持动态内容追加
 */

class LoadMorePagination extends HTMLElement {
  constructor() {
    super()

    // 获取必要的 DOM 元素
    this.button = this.querySelector('.load-more-button')
    this.progressBar = this.querySelector('.load-more-progress__bar')
    this.text = this.querySelector('.load-more-text')
    this.statusMessage = this.querySelector('.load-more-status')

    // 获取数据属性
    this.total = Number(this.dataset.total)
    this.loaded = Number(this.dataset.loaded)
    this.boxId = this.dataset.boxId

    // 获取商品列表容器
    this.grid = document.querySelector(this.boxId)

    // 绑定事件（只会执行一次）
    if (this.button && this.grid) {
      this.button.addEventListener('click', this.handleLoadMore.bind(this))
    }
  }

  /**
   * 处理 Load More 按钮点击
   */
  async handleLoadMore(event) {
    // 阻止默认跳转行为（支持 SEO 友好的 <a> 标签）
    event.preventDefault()

    const { nextUrl } = this.button.dataset
    if (!nextUrl) return

    // 禁用按钮，防止重复点击（兼容 <a> 和 <button> 标签）
    this.disableButton()
    this.button.textContent = 'Loading...'

    try {
      // 获取下一页内容
      const res = await fetch(nextUrl)

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`)
      }

      const html = await res.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')

      // 提取下一页的商品列表
      const nextGrid = doc.querySelector(this.boxId)
      if (!nextGrid) {
        console.error('Next page grid not found with selector:', this.boxId)
        return
      }

      // 记录当前列表中的产品数量（用于后续获取新加载的第一个产品）
      const previousProductCount = this.grid.children.length

      // 追加商品到当前列表
      this.grid.insertAdjacentHTML('beforeend', nextGrid.innerHTML)

      // 更新已加载数量
      const newProductsCount = nextGrid.children.length
      this.loaded = Math.min(this.total, this.loaded + newProductsCount)

      // 更新 UI
      this.updateProgress()
      this.updateText()

      // 【无障碍优化】通知屏幕阅读器用户新内容已加载
      this.announceToScreenReader(
        `Loaded ${newProductsCount} more products. Total ${this.loaded} of ${this.total} products shown.`
      )

      // 【无障碍优化】将焦点移至新加载的第一个产品
      this.moveFocusToNewContent(previousProductCount)

      // 检查是否有下一页
      const nextWrapper = doc.querySelector('load-more-pagination')
      const nextButton = nextWrapper?.querySelector('.load-more-button')

      if (nextButton && nextButton.dataset.nextUrl) {
        // 更新下一页 URL
        this.button.dataset.nextUrl = nextButton.dataset.nextUrl
        this.enableButton()
        this.button.textContent = 'Load More'
      } else {
        // 没有更多页面了
        this.button.remove()
        // 通知用户已加载全部产品
        this.announceToScreenReader('All products have been loaded.')
      }

      // 重新初始化 Trustpilot widgets（AJAX 加载后需要重新初始化）
      this.reinitializeTrustpilot()

      // 触发自定义事件，供其他脚本监听（如：图片懒加载、评论刷新等）
      this.dispatchEvent(
        new CustomEvent('load-more:complete', {
          bubbles: true,
          detail: {
            loaded: this.loaded,
            total: this.total,
            hasMore: !!nextButton,
            newProductsCount
          }
        })
      )
    } catch (error) {
      console.error('Load more failed:', error)
      this.enableButton()
      this.button.textContent = 'Load More'

      // 可选：显示错误提示
      this.showError('Failed to load more products. Please try again.')
    }
  }

  /**
   * 禁用按钮（兼容 <a> 和 <button> 标签）
   */
  disableButton() {
    this.button.style.pointerEvents = 'none'
    this.button.style.opacity = '0.6'
    this.button.setAttribute('aria-disabled', 'true')
    // 如果是 <button> 标签，也设置 disabled 属性
    if ('disabled' in this.button) {
      this.button.disabled = true
    }
  }

  /**
   * 启用按钮（兼容 <a> 和 <button> 标签）
   */
  enableButton() {
    this.button.style.pointerEvents = ''
    this.button.style.opacity = ''
    this.button.removeAttribute('aria-disabled')
    // 如果是 <button> 标签，也恢复 disabled 属性
    if ('disabled' in this.button) {
      this.button.disabled = false
    }
  }

  /**
   * 更新进度条
   */
  updateProgress() {
    if (!this.progressBar) return
    const percentage = Math.round((this.loaded / this.total) * 100)
    this.progressBar.style.width = `${percentage}%`
  }

  /**
   * 更新文案
   */
  updateText() {
    if (!this.text) return
    this.text.textContent = `1 - ${this.loaded} of ${this.total} products`
  }

  /**
   * 【无障碍】通知屏幕阅读器用户（通过 aria-live 区域）
   * @param {string} message - 要通知的消息
   */
  announceToScreenReader(message) {
    if (!this.statusMessage) return

    // 清空消息（确保相同消息也能触发通知）
    this.statusMessage.textContent = ''

    // 使用 setTimeout 确保 DOM 更新后再设置新消息
    setTimeout(() => {
      this.statusMessage.textContent = message
    }, 100)
  }

  /**
   * 【无障碍】将焦点移至新加载的第一个产品
   * @param {number} previousCount - 加载前的产品数量
   */
  moveFocusToNewContent(previousCount) {
    // 获取新加载的第一个产品元素
    const firstNewProduct = this.grid.children[previousCount]
    if (!firstNewProduct) return

    // 为新产品添加 tabindex（使其可获得焦点）
    firstNewProduct.setAttribute('tabindex', '-1')

    // 添加焦点样式（可选，避免突兀的焦点环）
    firstNewProduct.style.outline = '2px solid transparent'
    firstNewProduct.style.outlineOffset = '2px'

    // 将焦点移至该元素
    firstNewProduct.focus()

    // 滚动到该元素（确保在视口内，带平滑动画）
    firstNewProduct.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    })

    // 监听失焦事件，移除 tabindex（恢复默认行为）
    firstNewProduct.addEventListener(
      'blur',
      () => {
        firstNewProduct.removeAttribute('tabindex')
      },
      { once: true }
    )
  }

  /**
   * 显示错误提示（可选）
   */
  showError(message) {
    const errorEl = document.createElement('div')
    errorEl.className = 'load-more-error'
    errorEl.textContent = message
    errorEl.style.cssText = 'color: #d02e2e; margin-top: 1rem; text-align: center;'
    // 添加 role 和 aria-live 用于错误通知
    errorEl.setAttribute('role', 'alert')
    errorEl.setAttribute('aria-live', 'assertive')

    this.appendChild(errorEl)

    // 3秒后自动移除
    setTimeout(() => errorEl.remove(), 3000)
  }

  /**
   * 重新初始化 Trustpilot 评价组件
   * 在加载更多产品后调用，确保新产品的评价 widget 正常显示
   */
  reinitializeTrustpilot() {
    // 查找新加载产品中的所有 Trustpilot widget 并初始化
    const widgets = this.grid.querySelectorAll('.trustpilot-widget')
    widgets.forEach(widget => {
      try {
        window.Trustpilot.loadFromElement(widget, true)
      } catch (error) {
        console.warn('Trustpilot widget reload failed:', error)
      }
    })
  }
}

// 注册自定义元素
if (!customElements.get('load-more-pagination')) {
  customElements.define('load-more-pagination', LoadMorePagination)
}
